/**
 * components/wishlist/quick-add-sheet.tsx
 *
 * Bottom sheet for adding a new wishlist item.
 * Fields: title (required), note (optional), link URL (optional).
 * Vibe is pre-selected (passed as prop from the vibe list page),
 * or can be changed via a picker when used from the home FAB.
 */
import { useState } from 'react'
import type { WishlistVibe } from '@just-us/shared'
import VibeChip, { VIBE_META } from './vibe-chip'

interface QuickAddSheetProps {
  defaultVibe: WishlistVibe
  isSubmitting: boolean
  onAdd: (data: { vibe: WishlistVibe; title: string; note?: string; linkUrl?: string }) => void
  onClose: () => void
  allowVibeChange?: boolean
  initialTitle?: string
  initialNote?: string
  initialLinkUrl?: string
}

const ALL_VIBES: WishlistVibe[] = ['wander', 'watch', 'taste', 'weekend', 'try', 'together']

export default function QuickAddSheet({
  defaultVibe,
  isSubmitting,
  onAdd,
  onClose,
  allowVibeChange = false,
  initialTitle = '',
  initialNote = '',
  initialLinkUrl = '',
}: QuickAddSheetProps) {
  const [vibe, setVibe] = useState<WishlistVibe>(defaultVibe)
  const [title, setTitle] = useState(initialTitle)
  const [note, setNote] = useState(initialNote)
  const [linkUrl, setLinkUrl] = useState(initialLinkUrl)
  const [showAdvanced, setShowAdvanced] = useState(!!(initialNote || initialLinkUrl))

  const meta = VIBE_META[vibe]

  const handleSubmit = () => {
    if (!title.trim()) return
    onAdd({
      vibe,
      title: title.trim(),
      note: note.trim() || undefined,
      linkUrl: linkUrl.trim() || undefined,
    })
  }

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/20 z-[60] animate-fade-in"
        onClick={onClose}
      />

      {/* Sheet */}
      <div className="fixed bottom-0 left-0 right-0 max-w-[430px] mx-auto z-[70] bg-white rounded-t-[24px] shadow-[0_-4px_40px_rgba(0,0,0,0.12)] animate-slide-up pb-[env(safe-area-inset-bottom)]">
        {/* Handle */}
        <div className="flex justify-center pt-3 pb-1">
          <div className="w-[36px] h-[4px] rounded-full bg-[#F4C0D1]" />
        </div>

        <div className="px-5 pt-2 pb-5">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-['Playfair_Display'] text-[18px] font-medium text-[#2C1810]">
              Add to wishlist
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="w-[32px] h-[32px] rounded-full bg-[#F9E0E8] flex items-center justify-center text-[16px] text-[#B89AA2] active:bg-[#FBEAF0] touch-manipulation"
            >
              ×
            </button>
          </div>

          {/* Vibe selector (optional) */}
          {allowVibeChange && (
            <div className="mb-4">
              <p className="font-['DM_Sans'] text-[11px] text-[#B89AA2] mb-2 uppercase tracking-wide">Vibe</p>
              <div className="flex flex-wrap gap-2">
                {ALL_VIBES.map((v) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setVibe(v)}
                    className="transition-all duration-150 touch-manipulation"
                    style={{ opacity: vibe === v ? 1 : 0.5, transform: vibe === v ? 'scale(1.05)' : 'scale(1)' }}
                  >
                    <VibeChip vibe={v} />
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Current vibe indicator (non-changeable) */}
          {!allowVibeChange && (
            <div className="mb-4">
              <VibeChip vibe={vibe} />
            </div>
          )}

          {/* Title input */}
          <div className="mb-3">
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
              placeholder="What do you want to do together?"
              maxLength={300}
              autoFocus
              className="
                w-full h-[48px] px-4 rounded-[14px]
                bg-[#FAF8F4] border border-[#F9E0E8]
                font-['DM_Sans'] text-[15px] text-[#2C1810]
                placeholder:text-[#B89AA2]
                focus:outline-none focus:border-[#F4C0D1]
                transition-colors duration-150
              "
            />
          </div>

          {/* Toggle advanced */}
          <button
            type="button"
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="font-['DM_Sans'] text-[13px] text-[#B89AA2] mb-3 flex items-center gap-1 touch-manipulation"
          >
            <span style={{ transform: showAdvanced ? 'rotate(90deg)' : undefined, display: 'inline-block', transition: 'transform 0.15s' }}>›</span>
            {showAdvanced ? 'Hide' : 'Add note or link'}
          </button>

          {/* Advanced fields */}
          {showAdvanced && (
            <div className="flex flex-col gap-3 mb-3 animate-fade-in">
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Add a note… (optional)"
                maxLength={1000}
                rows={2}
                className="
                  w-full px-4 py-3 rounded-[14px]
                  bg-[#FAF8F4] border border-[#F9E0E8]
                  font-['DM_Sans'] text-[14px] text-[#2C1810]
                  placeholder:text-[#B89AA2]
                  focus:outline-none focus:border-[#F4C0D1]
                  transition-colors duration-150 resize-none
                "
              />
              <input
                type="url"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                placeholder="Paste a link… (optional)"
                className="
                  w-full h-[48px] px-4 rounded-[14px]
                  bg-[#FAF8F4] border border-[#F9E0E8]
                  font-['DM_Sans'] text-[14px] text-[#2C1810]
                  placeholder:text-[#B89AA2]
                  focus:outline-none focus:border-[#F4C0D1]
                  transition-colors duration-150
                "
              />
            </div>
          )}

          {/* Submit button */}
          <button
            type="button"
            disabled={!title.trim() || isSubmitting}
            onClick={handleSubmit}
            className="
              w-full h-[50px] rounded-full
              font-['DM_Sans'] text-[15px] font-medium text-white
              shadow-[0_4px_16px_rgba(0,0,0,0.15)]
              active:scale-[0.97] transition-all duration-150 touch-manipulation
              disabled:opacity-40
            "
            style={{ background: meta.accent }}
          >
            {isSubmitting ? (initialTitle ? 'Saving…' : 'Adding…') : (initialTitle ? 'Save changes' : 'Add to wishlist')}
          </button>
        </div>
      </div>
    </>
  )
}
